import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Checkbox, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import * as XLSX from 'xlsx';
import { throttle } from 'lodash';

import {
  useExcelUploadFileMutation,
  useExcelUploadRowMutation,
  useExcelUploadSheetMutation,
  useFinishExcelFileUploadMutation,
  useGetEmailOfAuthUserQuery,
} from '../../../gql/generated/graphql';
import { useSnackbar } from '../../../providers/SnackbarProvider';

type UploadExcelFileProps = {
  fileContents: ArrayBuffer | null;
  companyId: number;
  fileName: string;
  uploaderName: string;
  disableUpload?: boolean;
  onProgress?: (progress: number) => void;
  onSheetCompletion?: (sheetName: string) => void;
  onFileCompletion?: (fileID: number, userEmail: string) => void;
  validationSuccess?: boolean;
};

const BATCH_SIZE = 50;
const MAX_CONCURRENT_UPLOADS = 5;

const UploadExcelFile = ({
  fileContents,
  companyId,
  fileName,
  uploaderName,
  disableUpload = false,
  onProgress,
  onSheetCompletion,
  onFileCompletion,
  validationSuccess,
}: UploadExcelFileProps) => {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadExcelFile, { loading: uploadExcelFileLoading }] = useExcelUploadFileMutation();
  const [uploadExcelSheet, { loading: uploadExcelSheetLoading }] = useExcelUploadSheetMutation();
  const [uploadExcelRow, { loading: uploadExcelRowLoading }] = useExcelUploadRowMutation();
  const [finishFileUpload, { loading: fileUploadingMutationLoading }] = useFinishExcelFileUploadMutation();
  const [quickMode, setQuickMode] = useState(false);

  const sheetsToUpload: string[] = [
    'I_Steering',
    'PnL',
    'BS',
    'FTE',
    'Customers_aggregated',
    'Customers_list',
    'Pricing',
  ];
  const { user } = useAuthenticator();

  const authId = user.username.replace('GoogleWorkspace_', '');
  const {
    data: userEmail,
    loading,
    error,
  } = useGetEmailOfAuthUserQuery({
    variables: {
      authId: authId,
    },
  });

  const retryUpload = async (mutationFunc, variables, retries = 3) => {
    let attempt = 0;
    while (attempt < retries) {
      try {
        const response = await mutationFunc({ variables });
        if (response?.data) return response;
      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed`);
        attempt++;
        if (attempt >= retries) {
          showSnackbar('Failed after retries', 'error', false);
          throw error;
        }
      }
    }
  };

  const handleExcelUpload = async () => {
    if (!fileContents) {
      showSnackbar('No file contents provided', 'error', false);
      return;
    }

    // Reset progress at start
    if (onProgress) {
      onProgress(0);
    }

    setIsFileUploading(true);

    try {
      const workbook = XLSX.read(new Uint8Array(fileContents), { type: 'array' });
      const totalSheets = workbook.SheetNames.length;
      let totalProgress = 0;
      let currentRowNumber = 1; // Start at row 1

      const updateProgressThrottled = throttle(progress => {
        if (onProgress) {
          onProgress(progress);
        }
      }, 1000);

      // Step 1: Upload the file and get the fileId
      const fileResponse = await retryUpload(uploadExcelFile, {
        input: {
          companyId,
          fileName,
          uploaderName,
          isBenchmarkingFile: true,
          isCustomerFile: false,
          isLboFile: false,
        },
      });

      const fileId = fileResponse.data?.uploadExcelFile?.file?.id;
      if (!fileResponse.data?.uploadExcelFile?.ok || !fileId) {
        showSnackbar('Failed to create file record', 'error', false);
        return;
      }

      // Step 2: Upload sheets and rows
      const concurrentUploads = [];
      for (let i = 0; i < totalSheets; i++) {
        currentRowNumber = 1; // Reset row number for each sheet
        const sheetName = workbook.SheetNames[i];
        const sheet = workbook.Sheets[sheetName];

        if (!sheetsToUpload.includes(sheetName)) {
          continue; // Skip sheets not in the list
        }

        // Step 2a: Upload the sheet
        const sheetResponse = await retryUpload(uploadExcelSheet, {
          input: {
            excelFileId: Number(fileId),
            sheetName: sheetName,
          },
        });

        const sheetId = sheetResponse.data?.uploadExcelSheet?.sheet?.id;
        if (!sheetId) {
          showSnackbar('Failed to create sheet', 'error', false);
          continue;
        }

        // Step 2b: Upload rows in batches, passing the current starting row
        const rows: (string | number | null)[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        // Determine effective rows: stop after encountering 3 consecutive rows
        // where the first 14 cells are null/empty strings. Include those rows,
        // then skip any subsequent rows.
        let consecutiveEmpty = 0;
        let cutoffIndex = rows.length; // default: upload all rows
        for (let r = 0; r < rows.length; r++) {
          let isEmptyFirst14 = true;
          const row = rows[r] || [];
          for (let c = 0; c < 14; c++) {
            const cell = row[c];
            if (cell !== null && cell !== undefined && String(cell).trim() !== '') {
              isEmptyFirst14 = false;
              break;
            }
          }
          if (isEmptyFirst14) {
            consecutiveEmpty++;
            if (consecutiveEmpty >= 3) {
              cutoffIndex = r + 1; // include the third empty row, stop after
              break;
            }
          } else {
            consecutiveEmpty = 0;
          }
        }
        const effectiveRows = rows.slice(0, cutoffIndex);
        const totalRows = effectiveRows.length;

        for (let batchStart = 0; batchStart < totalRows; batchStart += BATCH_SIZE) {
          const batch = effectiveRows.slice(batchStart, batchStart + BATCH_SIZE);

          const batchPromise = retryUpload(uploadExcelRow, {
            input: {
              sheetId: Number(sheetId),
              startRow: currentRowNumber, // Pass the starting row for the batch
              rows: batch.map(row => ({
                cells: row.map(cell => (cell !== null && cell !== undefined ? String(cell) : '')),
              })),
            },
          });
          concurrentUploads.push(batchPromise);

          currentRowNumber += batch.length; // Increment row number by the number of rows in the batch

          // Update progress
          totalProgress += (BATCH_SIZE / (totalSheets * totalRows)) * 100;
          updateProgressThrottled(Math.min(totalProgress, 100));

          if (concurrentUploads.length >= MAX_CONCURRENT_UPLOADS) {
            await Promise.all(concurrentUploads);
            concurrentUploads.length = 0; // Reset after processing batch
          }
        }

        // Flush any remaining row uploads for this sheet
        if (concurrentUploads.length > 0) {
          await Promise.all(concurrentUploads);
          concurrentUploads.length = 0;
        }

        // Call the onSheetCompletion callback if provided
        if (onSheetCompletion) {
          onSheetCompletion(sheetName);
        }
      }

      // Extra safety: flush any remaining uploads
      if (concurrentUploads.length > 0) {
        await Promise.all(concurrentUploads);
        concurrentUploads.length = 0;
      }

      // Step 3: Mark the file as completed
      const finishFileResponse = await retryUpload(finishFileUpload, {
        input: parseInt(fileId, 10),
        onlyFinancialKpis: quickMode,
        userEmail: userEmail?.userEmailByAuthId,
      });

      if (!finishFileResponse?.data?.finishExcelFileUpload?.ok) {
        throw new Error('Failed to finish file upload');
      }

      // Set progress to 100% when complete
      if (onProgress) {
        onProgress(100);
      }

      // Call the onFileCompletion callback if provided
      if (onFileCompletion) {
        onFileCompletion(Number(fileId), userEmail?.userEmailByAuthId);
      }

      showSnackbar(t('benchmarking.uploadSuccess', 'File uploaded successfully'), 'success', false);
    } catch (err: any) {
      const message = err?.graphQLErrors?.[0]?.message || err?.message || 'Upload failed';
      showSnackbar(message, 'error', false);
    } finally {
      // Ensure loading state is cleared
      setIsFileUploading(false);
    }
  };

  const handleQuickMode = e => {
    const checked = e.target.checked;
    setQuickMode(checked);
  };

  const isUploading =
    isFileUploading ||
    fileUploadingMutationLoading ||
    uploadExcelFileLoading ||
    uploadExcelSheetLoading ||
    uploadExcelRowLoading;

  return (
    <div>
      <Button
        component="label"
        fullWidth
        variant="contained"
        disabled={isUploading || disableUpload}
        sx={{ flexGrow: 1 }}
        onClick={handleExcelUpload}
      >
        {isUploading ? (
          <Box display="flex" gap={2}>
            <Typography>{t('benchmarking.uploadFileLoadingIndicator')}</Typography>
            <CircularProgress size={20} color="secondary" />
          </Box>
        ) : (
          t('benchmarking.uploadToDBButton')
        )}
      </Button>

      <Box display="flex" flexDirection="column" gap={2}>
        {fileContents && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ color: 'initial' }}>{t('benchmarking.enableQuickMode', 'Enable Quick Mode')}</Typography>
            <Tooltip title={t('benchmarking.quickModeTooltip', 'Only process financial KPIs')}>
              <Box sx={{ ml: 1 }}>
                <Checkbox
                  checked={quickMode}
                  onChange={handleQuickMode}
                  color="primary"
                  size="small"
                  disabled={!validationSuccess}
                />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default UploadExcelFile;
